import type { SVGProps } from "react"
import type { IconName } from "@/config/types"

/**
 * Small, dependency free icon set (stroke based, 24x24 grid). Unknown names
 * fall back to a neutral dot so a custom configuration can never break a view.
 */
const PATHS: Record<string, string> = {
  home: "M3 10.5 12 3l9 7.5M5 9.5V21h14V9.5",
  compass: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm3.5-12.5-2 5-5 2 2-5 5-2Z",
  relay: "M12 3v4m0 10v4M5 12H3m18 0h-2M7.3 7.3 5.9 5.9m12.2 12.2-1.4-1.4M16.7 7.3l1.4-1.4M5.9 18.1l1.4-1.4M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z",
  bell: "M18 8a6 6 0 1 0-12 0c0 6-2 7-2 7h16s-2-1-2-7M10.3 21a2 2 0 0 0 3.4 0",
  bookmark: "M6 3h12v18l-6-4.5L6 21V3Z",
  search: "M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm10 2-4.35-4.35",
  filter: "M4 6h16M7 12h10M10 18h4",
  settings:
    "M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm7.4-2.1.8 1.6-2 3.4-1.8-.4a7.6 7.6 0 0 1-2.4 1.4l-.6 1.7h-4l-.6-1.7a7.6 7.6 0 0 1-2.4-1.4l-1.8.4-2-3.4.8-1.6a7.7 7.7 0 0 1 0-2.8l-.8-1.6 2-3.4 1.8.4A7.6 7.6 0 0 1 8.8 4l.6-1.7h4l.6 1.7a7.6 7.6 0 0 1 2.4 1.4l1.8-.4 2 3.4-.8 1.6a7.7 7.7 0 0 1 0 2.8Z",
  user: "M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z",
  users: "M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9.5 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM22 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8",
  globe: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0-18c2.5 2.5 3.8 5.6 3.8 9S14.5 18.5 12 21c-2.5-2.5-3.8-5.6-3.8-9S9.5 5.5 12 3ZM3.5 9h17M3.5 15h17",
  star: "m12 3.5 2.7 5.6 6.1.9-4.4 4.3 1 6.1-5.4-2.9-5.4 2.9 1-6.1L3.2 10l6.1-.9L12 3.5Z",
  pen: "M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z",
  document: "M14 3H6v18h12V7l-4-4Zm0 0v4h4M9 13h6M9 17h6",
  plus: "M12 5v14M5 12h14",
  "chevron-down": "m6 9 6 6 6-6",
  "chevron-right": "m9 6 6 6-6 6",
  "chevron-left": "m15 6-6 6 6 6",
  "arrow-left": "M20 12H4m0 0 6-6m-6 6 6 6",
  "arrow-right": "M4 12h16m0 0-6-6m6 6-6 6",
  external: "M14 4h6v6M20 4l-8 8M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5",
  link: "M10 13a5 5 0 0 0 7 0l2-2a5 5 0 0 0-7-7l-1 1M14 11a5 5 0 0 0-7 0l-2 2a5 5 0 0 0 7 7l1-1",
  image: "M4 4h16v16H4zM8.5 9.5a1.2 1.2 0 1 0 0-2.4 1.2 1.2 0 0 0 0 2.4ZM20 15l-4.5-4.5L6 20",
  heart: "M12 20s-7-4.4-7-9.3A4 4 0 0 1 12 8a4 4 0 0 1 7 2.7C19 15.6 12 20 12 20Z",
  repeat: "M17 2l4 4-4 4M3 11V9a4 4 0 0 1 4-4h14M7 22l-4-4 4-4m14-3v2a4 4 0 0 1-4 4H3",
  reply: "M9 14 4 9l5-5M4 9h9a7 7 0 0 1 7 7v3",
  more: "M6 12h.01M12 12h.01M18 12h.01",
  x: "M6 6l12 12M18 6 6 18",
  check: "M4 12.5 9 17.5 20 6.5",
  trash: "M4 7h16M9 7V4h6v3m-8 0 1 14h8l1-14M10 11v6M14 11v6",
  pencil: "M4 20h4L20 8l-4-4L4 16v4Zm10-14 4 4",
  refresh: "M3 12a9 9 0 0 1 15.5-6.2M21 4v5h-5M21 12a9 9 0 0 1-15.5 6.2M3 20v-5h5",
  copy: "M9 9h11v11H9zM5 15V4h11",
  logout: "M15 4h4v16h-4M10 16l-4-4 4-4M6 12h9",
  sun: "M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm0-14v2m0 18v-2M3 12h2m14 0h2M5.6 5.6 7 7m10 10 1.4 1.4M18.4 5.6 17 7M7 17l-1.4 1.4",
  moon: "M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5Z",
  menu: "M4 6h16M4 12h16M4 18h16",
  info: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0-13h.01M11 12h1v5h1",
  alert: "M12 3 2.5 20h19L12 3Zm0 6v5m0 3h.01",
  code: "m9 8-4 4 4 4m6-8 4 4-4 4",
  zap: "m13 2-9 12h7l-1 8 9-12h-7l1-8Z",
  eye: "M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6Zm10 2.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z",
  "eye-off": "M3 3l18 18M10.6 10.6A2.5 2.5 0 0 0 12 15c.7 0 1.3-.3 1.8-.7M6.3 6.8C3.7 8.6 2 12 2 12s3.5 6 10 6c1.4 0 2.6-.3 3.8-.8M9.9 5.2A11 11 0 0 1 12 5c6.5 0 10 6 10 6a19 19 0 0 1-2.6 3.4",
  dot: "M12 12h.01",
  smile: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM8.5 14a4 4 0 0 0 7 0M9 9.5h.01M15 9.5h.01",
  lock: "M6 11h12v10H6zM9 11V7.5a3 3 0 0 1 6 0V11",
  help: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm-2.4-10.6a2.4 2.4 0 1 1 3.4 2.2c-.7.4-1 .9-1 1.7M12 17h.01",
  list: "M8.5 6H21M8.5 12H21M8.5 18H21M4 6h.01M4 12h.01M4 18h.01",
  notes: "M5 4h9l5 5v11H5zM14 4v5h5M8 13h8M8 17h5",
  waves: "M12 12h.01M8.6 8.6a4.8 4.8 0 0 0 0 6.8M15.4 8.6a4.8 4.8 0 0 1 0 6.8M5.6 5.6a9 9 0 0 0 0 12.8M18.4 5.6a9 9 0 0 1 0 12.8",
  puzzle: "M9.5 4a2 2 0 0 1 4 0v1.5H17v3h-1a2 2 0 1 0 0 4h1v3h-3.5V21a2 2 0 1 1-4 0v-2.5H6v-3h1a2 2 0 1 0 0-4H6v-3h3.5V4Z",
  key: "M15 4a5 5 0 1 0-1.9 9.6L4 22H2v-3.4l2-2H6v-2h2v-2l1.4-1.4A5 5 0 0 0 15 4Z",
  server: "M5 4h14v6H5zM5 14h14v6H5zM8.5 7h.01M8.5 17h.01",
  download: "M12 4v10m0 0 4-4m-4 4-4-4M4 17v3h16v-3",
}

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, "name"> {
  name: IconName | string
  size?: number
}

export function Icon({ name, size = 18, ...props }: IconProps) {
  const path = PATHS[name] ?? PATHS.dot
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      <path d={path} />
    </svg>
  )
}
